import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Save, Eye, Wand2, Send, Bold, Italic, Heading, Link, Image, Code, List, ChevronRight, ChevronDown, Sparkles, Upload, AlignLeft, AlignCenter, AlignRight, Square, ShieldCheck, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const EDITORS = [
  { value: 'daniel_dobos', label: 'Daniel Dobos — CTO, ADD' },
  { value: 'adriana_dobos', label: 'Adriana Dobos — Founder, ADD' },
  { value: 'marcus_webb', label: 'Marcus Webb — Ex-Reuters' },
  { value: 'elena_vasilescu', label: 'Elena Vasilescu — Ex-Digi24' },
  { value: 'james_chen', label: 'James Chen — Ex-Wired' },
  { value: 'sofia_marinescu', label: 'Sofia Marinescu — Ex-Nature' },
  { value: 'daniel_novak', label: 'Daniel Novak — Ex-Ars Technica' },
];

const EDITOR_NAMES: Record<string, string> = {
  daniel_dobos: 'Daniel Dobos', adriana_dobos: 'Adriana Dobos', marcus_webb: 'Marcus Webb',
  elena_vasilescu: 'Elena Vasilescu', james_chen: 'James Chen', sofia_marinescu: 'Sofia Marinescu', daniel_novak: 'Daniel Novak',
};

const AI_CHIPS = [
  { action: 'write_intro', label: 'Write Intro' },
  { action: 'improve', label: 'Improve' },
  { action: 'seo_optimize', label: 'SEO Optimize' },
  { action: 'translate_ro', label: 'Translate RO' },
  { action: 'suggest_titles', label: 'Suggest Titles' },
  { action: 'expand', label: 'Expand' },
  { action: 'grammar_fix', label: 'Grammar Fix' },
  { action: 'humanize', label: 'Humanize' },
  { action: 'generate_tags', label: 'Generate Tags' },
];

const CATEGORIES = ['news', 'tech', 'ai', 'innovation', 'opinion'];
const WORD_COUNTS = [800, 1200, 1800, 2500, 3500];
const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Simple markdown to HTML converter
const mdToHtml = (md: string): string => {
  let html = md
    .replace(/^### (.+)$/gm, '<h3 style="font-family:var(--f-display);color:var(--ink);margin:1.5em 0 .5em;font-size:1.25rem">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-family:var(--f-display);color:var(--ink);margin:1.8em 0 .6em;font-size:1.5rem">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:.9em">$1</code>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" style="color:var(--blue);text-decoration:underline">$1</a>')
    .replace(/^- (.+)$/gm, '<li style="margin-left:1.5em;margin-bottom:.3em">$1</li>')
    .replace(/\n\n/g, '</p><p style="margin-bottom:1em;line-height:1.85">')
    .replace(/\n/g, '<br/>');
  // Wrap in paragraph if not starting with HTML tag
  if (!html.startsWith('<')) html = '<p style="margin-bottom:1em;line-height:1.85">' + html + '</p>';
  return html;
};

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const isEdit = !!id;
  const fromRssId = searchParams.get('from_rss');
  const coverInputRef = useRef<HTMLInputElement>(null);
  const inlineInputRef = useRef<HTMLInputElement>(null);

  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [showPreview, setShowPreview] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState<Array<{ role: string; content: string }>>([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [genOpen, setGenOpen] = useState(!isEdit);
  const [genPrompt, setGenPrompt] = useState('');
  const [genWordCount, setGenWordCount] = useState('1800');
  const [genEditor, setGenEditor] = useState('daniel_dobos');
  const [genCategory, setGenCategory] = useState('tech');
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgAlign, setImgAlign] = useState<string | null>(null);
  const [qualityChecking, setQualityChecking] = useState(false);
  const [qualityResult, setQualityResult] = useState<any>(null);

  const [form, setForm] = useState({
    title_en: '', title_ro: '', slug: '', excerpt_en: '', excerpt_ro: '',
    summary_en: '', summary_ro: '',
    content_en: '', content_ro: '', tags: '' as string, cover_image: '',
    status: 'draft', category: 'news', author_name: 'Daniel Dobos',
    seo_title_en: '', seo_title_ro: '', seo_description_en: '', seo_description_ro: '',
  });

  const { data: post } = useQuery({
    queryKey: ['blog_post', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await supabase.from('blog_posts').select('*').eq('id', id).single();
      return data;
    },
    enabled: isEdit,
  });

  // Fetch scraped article when coming from RSS Scraper
  const { data: rssArticle } = useQuery({
    queryKey: ['scraped_article', fromRssId],
    queryFn: async () => {
      const { data } = await supabase.from('scraped_articles').select('*').eq('id', fromRssId!).maybeSingle();
      return data;
    },
    enabled: !!fromRssId && !isEdit,
  });

  // Gate: if from RSS but article isn't rewritten with content, block
  const rssNotReady = !!fromRssId && !isEdit && rssArticle !== undefined && (
    !rssArticle ||
    rssArticle.status !== 'rewritten' ||
    !rssArticle.rewritten_en || rssArticle.rewritten_en.length < 100 ||
    !rssArticle.rewritten_ro || rssArticle.rewritten_ro.length < 100
  );

  useEffect(() => {
    if (post) {
      setForm({
        title_en: post.title_en || '', title_ro: post.title_ro || '', slug: post.slug || '',
        excerpt_en: post.excerpt_en || '', excerpt_ro: post.excerpt_ro || '',
        summary_en: (post as any).summary_en || '', summary_ro: (post as any).summary_ro || '',
        content_en: post.content_en || '', content_ro: post.content_ro || '',
        tags: (post.tags || []).join(', '), cover_image: post.cover_image || '',
        status: post.status || 'draft', category: (post as any).category || 'news',
        author_name: (post as any).author_name || 'Daniel Dobos',
        seo_title_en: post.seo_title_en || '', seo_title_ro: post.seo_title_ro || '',
        seo_description_en: post.seo_description_en || '', seo_description_ro: post.seo_description_ro || '',
      });
    }
  }, [post]);

  useEffect(() => {
    if (rssArticle && !isEdit) {
      setForm({
        title_en: rssArticle.title_en || rssArticle.original_title || '',
        title_ro: rssArticle.title_ro || '',
        slug: slugify(rssArticle.title_en || rssArticle.original_title || ''),
        content_en: rssArticle.rewritten_en || '',
        content_ro: rssArticle.rewritten_ro || '',
        excerpt_en: rssArticle.excerpt_en || '',
        excerpt_ro: rssArticle.excerpt_ro || '',
        summary_en: rssArticle.summary_en || '',
        summary_ro: rssArticle.summary_ro || '',
        seo_title_en: rssArticle.seo_title_en || '',
        seo_title_ro: rssArticle.seo_title_ro || '',
        seo_description_en: rssArticle.seo_description_en || '',
        seo_description_ro: rssArticle.seo_description_ro || '',
        tags: (rssArticle.rewrite_tags || []).join(', '),
        cover_image: '', status: 'draft', category: 'news',
        author_name: 'Marcus Webb',
      });
      setGenOpen(false);
    }
  }, [rssArticle]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'title_en' && !isEdit) next.slug = slugify(value);
      return next;
    });
  };

  const content = lang === 'en' ? form.content_en : form.content_ro;
  const wordCount = content.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  // Upload image to blog-images bucket
  const uploadImage = async (file: File): Promise<string | null> => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('blog-images').upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from('blog-images').getPublicUrl(path);
      return data.publicUrl;
    } catch (e: any) {
      toast.error('Upload failed: ' + e.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) handleChange('cover_image', url);
  };

  const handleInlineImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !imgAlign) return;
    const url = await uploadImage(file);
    if (!url) return;
    const field = lang === 'en' ? 'content_en' : 'content_ro';
    let imgHtml = '';
    switch (imgAlign) {
      case 'left': imgHtml = `\n<div style="float:left;margin:0 16px 8px 0;max-width:45%"><img src="${url}" alt="" /></div>\n`; break;
      case 'center': imgHtml = `\n<div style="text-align:center"><img src="${url}" alt="" style="max-width:80%" /></div>\n`; break;
      case 'right': imgHtml = `\n<div style="float:right;margin:0 0 8px 16px;max-width:45%"><img src="${url}" alt="" /></div>\n`; break;
      case 'block': imgHtml = `\n<img src="${url}" alt="" style="width:100%;border-radius:8px" />\n`; break;
    }
    handleChange(field, content + imgHtml);
    setImgAlign(null);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: any = {
        title_en: form.title_en, title_ro: form.title_ro, slug: form.slug,
        excerpt_en: form.excerpt_en, excerpt_ro: form.excerpt_ro,
        summary_en: form.summary_en || null, summary_ro: form.summary_ro || null,
        content_en: form.content_en, content_ro: form.content_ro,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        cover_image: form.cover_image || null, status: form.status,
        category: form.category, author_name: form.author_name,
        seo_title_en: form.seo_title_en || null, seo_title_ro: form.seo_title_ro || null,
        seo_description_en: form.seo_description_en || null, seo_description_ro: form.seo_description_ro || null,
        reading_time_min: readingTime,
        published_at: form.status === 'published' ? new Date().toISOString() : null,
      };

      // Slug deduplication for new posts
      if (!isEdit) {
        let finalSlug = payload.slug;
        const { data: existing } = await supabase
          .from('blog_posts')
          .select('slug')
          .like('slug', `${finalSlug}%`);
        if (existing?.some(p => p.slug === finalSlug)) {
          let i = 2;
          while (existing.some(p => p.slug === `${finalSlug}-${i}`)) i++;
          finalSlug = `${finalSlug}-${i}`;
        }
        payload.slug = finalSlug;
      }

      if (isEdit) {
        const { error } = await supabase.from('blog_posts').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('blog_posts').insert(payload);
        if (error) throw error;
        // Mark RSS article as published if coming from RSS Scraper
        if (fromRssId) {
          await supabase.from('scraped_articles').update({ status: 'published' }).eq('id', fromRssId);
        }
      }
    },
    onSuccess: () => { toast.success('Post saved!'); qc.invalidateQueries({ queryKey: ['blog_posts'] }); if (!isEdit) navigate('/admin/blog'); },
    onError: (e: any) => toast.error(e.message),
  });

  // AI Generate full article
  const generateArticle = async () => {
    if (!genPrompt.trim()) { toast.error('Please describe your article idea'); return; }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-generate-article', {
        body: { prompt: genPrompt, word_count: parseInt(genWordCount), editor: genEditor, category: genCategory },
      });
      if (error) throw error;
      setForm(prev => ({
        ...prev,
        title_en: data.title_en || prev.title_en, title_ro: data.title_ro || prev.title_ro,
        slug: data.slug || prev.slug, excerpt_en: data.excerpt_en || prev.excerpt_en,
        excerpt_ro: data.excerpt_ro || prev.excerpt_ro,
        summary_en: data.summary_en || prev.summary_en, summary_ro: data.summary_ro || prev.summary_ro,
        content_en: data.content_en || prev.content_en,
        content_ro: data.content_ro || prev.content_ro, tags: (data.tags || []).join(', '),
        category: genCategory, author_name: data.author_name || EDITOR_NAMES[genEditor] || prev.author_name,
        seo_title_en: data.seo_title_en || prev.seo_title_en, seo_title_ro: data.seo_title_ro || prev.seo_title_ro,
        seo_description_en: data.seo_description_en || prev.seo_description_en, seo_description_ro: data.seo_description_ro || prev.seo_description_ro,
      }));
      toast.success('Article generated! Review and edit before saving.');
      setGenOpen(false);
    } catch (e: any) { toast.error(e.message); } finally { setGenerating(false); }
  };

  const callAI = async (action: string, prompt?: string) => {
    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-blog-assistant', {
        body: { action, content, prompt: prompt || '' },
      });
      if (error) throw error;
      setAiMessages(prev => [...prev, { role: 'assistant', content: data.result }]);
    } catch (e: any) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  const checkQuality = async () => {
    if (!content || content.length < 100) { toast.error('Content too short to check'); return; }
    setQualityChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-content-quality', {
        body: { content, language: lang, check_plagiarism: true },
      });
      if (error) throw error;
      setQualityResult(data);
      if (data.passed) {
        toast.success(`Quality check passed! AI score: ${data.ai_score}, Plagiarism: ${data.plagiarism_score}%`);
      } else {
        toast.warning(`Quality concerns detected. AI score: ${data.ai_score}, Plagiarism: ${data.plagiarism_score}%`);
      }
    } catch (e: any) { toast.error(e.message); } finally { setQualityChecking(false); }
  };

  const insertToolbar = (prefix: string, suffix: string) => {
    const field = lang === 'en' ? 'content_en' : 'content_ro';
    handleChange(field, content + prefix + suffix);
  };

  if (rssNotReady) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>New Post</h1>
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <p className="text-lg font-medium" style={{ color: 'var(--ink)' }}>Rewrite not completed yet</p>
            <p className="text-sm text-muted-foreground">The AI rewrite for this article hasn't finished or produced valid content. Please go back to the RSS Scraper and wait for the rewrite to complete.</p>
            <Button variant="outline" onClick={() => navigate('/admin/rss')}>Back to RSS Scraper</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>{isEdit ? 'Edit Post' : 'New Post'}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)} className="gap-2">
            <Eye className="w-4 h-4" /> {showPreview ? 'Editor' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={() => setShowAI(!showAI)} className="gap-2">
            <Wand2 className="w-4 h-4" /> AI Assistant
          </Button>
          <Button variant="outline" onClick={checkQuality} disabled={qualityChecking || !content} className="gap-2">
            {qualityChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Check Quality
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="gap-2">
            <Save className="w-4 h-4" /> Save
          </Button>
        </div>
      </div>

      {/* Quality Score Display */}
      {qualityResult && (
        <div className={`p-3 rounded-lg border flex items-center gap-4 text-sm ${qualityResult.passed ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
          <div className="flex items-center gap-2">
            <span className="font-medium">AI Score:</span>
            <Badge variant={qualityResult.ai_score <= 30 ? 'default' : qualityResult.ai_score <= 50 ? 'secondary' : 'destructive'}
              className={qualityResult.ai_score <= 30 ? 'bg-green-600' : ''}>
              {qualityResult.ai_score}/100
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Plagiarism:</span>
            <Badge variant={qualityResult.plagiarism_score <= 5 ? 'default' : qualityResult.plagiarism_score <= 15 ? 'secondary' : 'destructive'}
              className={qualityResult.plagiarism_score <= 5 ? 'bg-green-600' : ''}>
              {qualityResult.plagiarism_score}%
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Heuristic:</span>
            <span>{qualityResult.heuristic_score}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Adversarial:</span>
            <span>{qualityResult.adversarial_score}</span>
          </div>
          {qualityResult.adversarial_flags?.length > 0 && (
            <details className="ml-auto">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                {qualityResult.adversarial_flags.length} flagged patterns
              </summary>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {qualityResult.adversarial_flags.map((f: any, i: number) => (
                  <p key={i} className="text-xs"><strong>{f.sentence?.substring(0, 60)}</strong>: {f.reason}</p>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* AI Article Generator */}
      <Collapsible open={genOpen} onOpenChange={setGenOpen}>
        <Card style={{ border: '2px solid var(--amber)' }}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: 'var(--amber)' }} />
                AI Article Generator
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${genOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <Textarea placeholder="Describe your article idea in detail… The more context you provide, the better the result." value={genPrompt} onChange={e => setGenPrompt(e.target.value)} className="min-h-[100px] text-sm" />
              <div className="grid grid-cols-4 gap-3">
                <Select value={genEditor} onValueChange={setGenEditor}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Editor" /></SelectTrigger>
                  <SelectContent>{EDITORS.map(e => <SelectItem key={e.value} value={e.value} className="text-xs">{e.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={genWordCount} onValueChange={setGenWordCount}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Words" /></SelectTrigger>
                  <SelectContent>{WORD_COUNTS.map(w => <SelectItem key={w} value={w.toString()} className="text-xs">{w} words</SelectItem>)}</SelectContent>
                </Select>
                <Select value={genCategory} onValueChange={setGenCategory}>
                  <SelectTrigger className="text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={generateArticle} disabled={generating || !genPrompt.trim()} className="gap-2 text-sm">
                  <Sparkles className="w-4 h-4" /> {generating ? 'Generating…' : 'Generate Article'}
                </Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <div className="flex gap-6">
        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input placeholder="Title (EN)" value={form.title_en} onChange={e => handleChange('title_en', e.target.value)} />
            <Input placeholder="Title (RO)" value={form.title_ro} onChange={e => handleChange('title_ro', e.target.value)} />
          </div>
          <div className="grid grid-cols-5 gap-4">
            <Input placeholder="Slug" value={form.slug} onChange={e => handleChange('slug', e.target.value)} />
            {/* Cover image upload */}
            <div className="flex items-center gap-2">
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
              <Button variant="outline" size="sm" className="gap-1 text-xs w-full" onClick={() => coverInputRef.current?.click()} disabled={uploading}>
                <Upload className="w-3 h-3" /> {form.cover_image ? 'Change Cover' : 'Upload Cover'}
              </Button>
              {form.cover_image && <img src={form.cover_image} alt="" className="w-8 h-8 rounded object-cover" />}
            </div>
            <Select value={form.status} onValueChange={v => handleChange('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.category} onValueChange={v => handleChange('category', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={Object.keys(EDITOR_NAMES).find(k => EDITOR_NAMES[k] === form.author_name) || 'daniel_dobos'} onValueChange={v => handleChange('author_name', EDITOR_NAMES[v] || v)}>
              <SelectTrigger className="text-xs"><SelectValue placeholder="Author" /></SelectTrigger>
              <SelectContent>{EDITORS.map(e => <SelectItem key={e.value} value={e.value} className="text-xs">{e.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input placeholder="Excerpt (EN)" value={form.excerpt_en} onChange={e => handleChange('excerpt_en', e.target.value)} />
            <Input placeholder="Excerpt (RO)" value={form.excerpt_ro} onChange={e => handleChange('excerpt_ro', e.target.value)} />
          </div>
          <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => handleChange('tags', e.target.value)} />
          <div className="grid grid-cols-2 gap-4">
            <Textarea placeholder="Summary (EN) — auto-generated abstract" value={form.summary_en} onChange={e => handleChange('summary_en', e.target.value)} className="min-h-[80px] text-sm" />
            <Textarea placeholder="Summary (RO) — rezumat auto-generat" value={form.summary_ro} onChange={e => handleChange('summary_ro', e.target.value)} className="min-h-[80px] text-sm" />
          </div>

          <details className="group">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">SEO Fields</summary>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <Input placeholder="SEO Title (EN)" value={form.seo_title_en} onChange={e => handleChange('seo_title_en', e.target.value)} />
              <Input placeholder="SEO Title (RO)" value={form.seo_title_ro} onChange={e => handleChange('seo_title_ro', e.target.value)} />
              <Input placeholder="SEO Description (EN)" value={form.seo_description_en} onChange={e => handleChange('seo_description_en', e.target.value)} />
              <Input placeholder="SEO Description (RO)" value={form.seo_description_ro} onChange={e => handleChange('seo_description_ro', e.target.value)} />
            </div>
          </details>

          <Tabs value={lang} onValueChange={v => setLang(v as 'en' | 'ro')}>
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="en">English</TabsTrigger>
                <TabsTrigger value="ro">Română</TabsTrigger>
              </TabsList>
              <div className="flex gap-1">
                {[{ icon: Bold, p: '**', s: '**' }, { icon: Italic, p: '_', s: '_' }, { icon: Heading, p: '\n## ', s: '' }, { icon: Link, p: '[', s: '](url)' }, { icon: Code, p: '`', s: '`' }, { icon: List, p: '\n- ', s: '' }].map(({ icon: Icon, p, s }, i) => (
                  <Button key={i} variant="ghost" size="icon" onClick={() => insertToolbar(p, s)} className="w-8 h-8">
                    <Icon className="w-3.5 h-3.5" />
                  </Button>
                ))}
                {/* Image upload with alignment popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-8 h-8"><Image className="w-3.5 h-3.5" /></Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2">
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--ink)' }}>Image alignment</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { align: 'left', icon: AlignLeft, label: 'Left' },
                        { align: 'center', icon: AlignCenter, label: 'Center' },
                        { align: 'right', icon: AlignRight, label: 'Right' },
                        { align: 'block', icon: Square, label: 'Full Width' },
                      ].map(({ align, icon: Icon, label }) => (
                        <Button key={align} variant="outline" size="sm" className="gap-1 text-xs" onClick={() => { setImgAlign(align); inlineInputRef.current?.click(); }}>
                          <Icon className="w-3 h-3" /> {label}
                        </Button>
                      ))}
                    </div>
                    <input ref={inlineInputRef} type="file" accept="image/*" className="hidden" onChange={handleInlineImageUpload} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <TabsContent value="en">
              {showPreview ? (
                <div className="p-6 rounded-lg min-h-[400px] border" style={{ fontFamily: 'var(--f-body)', color: 'var(--ink)' }} dangerouslySetInnerHTML={{ __html: mdToHtml(form.content_en) }} />
              ) : (
                <Textarea value={form.content_en} onChange={e => handleChange('content_en', e.target.value)} className="min-h-[400px] font-mono text-sm" placeholder="Write your post in Markdown…" />
              )}
            </TabsContent>
            <TabsContent value="ro">
              {showPreview ? (
                <div className="p-6 rounded-lg min-h-[400px] border" style={{ fontFamily: 'var(--f-body)', color: 'var(--ink)' }} dangerouslySetInnerHTML={{ __html: mdToHtml(form.content_ro) }} />
              ) : (
                <Textarea value={form.content_ro} onChange={e => handleChange('content_ro', e.target.value)} className="min-h-[400px] font-mono text-sm" placeholder="Scrie postarea în Markdown…" />
              )}
            </TabsContent>
          </Tabs>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>{wordCount} words</span>
            <span>{readingTime} min read</span>
            <span>Author: {form.author_name}</span>
          </div>
        </div>

        {showAI && (
          <div className="w-80 space-y-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Wand2 className="w-4 h-4" /> AI Assistant</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {AI_CHIPS.map(chip => (
                    <Button key={chip.action} variant="outline" size="sm" disabled={aiLoading} onClick={() => callAI(chip.action)} className="text-xs h-7">
                      {chip.label}
                    </Button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Ask anything…" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { callAI('free_chat', aiPrompt); setAiPrompt(''); } }} className="text-xs" />
                  <Button size="icon" disabled={aiLoading || !aiPrompt} onClick={() => { callAI('free_chat', aiPrompt); setAiPrompt(''); }}>
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {aiLoading && <p className="text-xs text-muted-foreground animate-pulse">Thinking…</p>}
                <div className="max-h-80 overflow-y-auto space-y-3">
                  {aiMessages.map((msg, i) => (
                    <div key={i} className="p-3 rounded-lg text-xs whitespace-pre-wrap" style={{ background: 'var(--off-white)', color: 'var(--ink)' }}>
                      {msg.content}
                      <Button size="sm" variant="ghost" className="mt-2 text-xs text-primary h-6" onClick={() => handleChange(lang === 'en' ? 'content_en' : 'content_ro', content + '\n\n' + msg.content)}>
                        Apply <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogEditor;
